// tslint:disable:forin
import { strEnum } from "../../types";

export interface ITCISwaggerContact {
    name?: string;
    url?: string;
    email?: string;
}

export interface ITCISwaggerLicense {
    name: string;
    url?: string;
}

export interface ITCISwaggerInfo {
    title: string;
    description?: string;
    termsOfService?: string;
    contact?: ITCISwaggerContact;
    license?: ITCISwaggerLicense;
    version: string;
}

export interface ITCISwaggerExternalDocs {
    description?: string;
    url: string;
}

export const PARAMETER_IN = strEnum(["query", "header", "path", "formData", "body"]);

export type PARAMETER_IN = keyof typeof PARAMETER_IN;

export interface ISwaagerParameterItems {
    type: string;
    format?: string;
    items?: ISwaagerParameterItems;
    collectionFormat?: string;
    default?: any;
    maximum?: number;
    exclusiveMaximum?: boolean;
    minimum?: number;
    exclusiveMinimum?: boolean;
    maxLength?: number;
    minLength?: number;
    pattern?: string;
    maxItems?: number;
    minItems?: number;
    uniqueItems?: boolean;
    enum?: any[];
    multipleOf?: number;
}

export interface ITCISwaggerParameter {
    name: string;
    in: PARAMETER_IN;
    description?: string;
    required?: boolean;
    schema?: any;
    type?: string;
    format?: string;
    allowEmptyValue?: boolean;
    items?: ISwaagerParameterItems;
    collectionFormat?: string;
    default?: any;
    maximum?: number;
    exclusiveMaximum?: boolean;
    minimum?: number;
    exclusiveMinimum?: boolean;
    maxLength?: number;
    minLength?: number;
    pattern?: string;
    maxItems?: number;
    minItems?: number;
    uniqueItems?: boolean;
    enum?: any[];
    multipleOf?: number;
}

export interface ITCISwaggerMethods {
    tags?: string[];
    summary?: string;
    description?: string;
    externalDocs?: ITCISwaggerExternalDocs;
    operationId?: string;
    consumes?: string[];
    produces?: string[];
    parameters?: ITCISwaggerParameter[] | ITCISwaggerReference[];
    responses: ITCISwaggerResponseDictionary;
    schemes?: string[];
    deprecated?: boolean;
    security?: ITCISwaggerSecurity[];
}

export interface ITCISwaggerMethodsDictionary {
    [method: string]: string | ITCISwaggerMethods | ITCISwaggerParameter[] | ITCISwaggerReference[];
}

export interface ITCISwaggerPathsDictionary {
    [path: string]: ITCISwaggerMethodsDictionary;
}

export interface ITCISwaggerHeadersDictionary {
    [header: string]: ITCISwaggerHeader;
}

export interface ITCISwaggerExamplesDictionary {
    [mimeType: string]: any;
}

export interface ITCISwaggerHeader extends ISwaagerParameterItems {
    description?: string;
}

export interface ITCISwaggerResponse {
    description: string;
    schema?: any;
    headers?: ITCISwaggerHeadersDictionary;
    examples?: ITCISwaggerExamplesDictionary;
}

export interface ITCISwaggerResponseDictionary {
    [responseCode: string]: ITCISwaggerResponse | ITCISwaggerReference;
}

export interface ITCISwaggerTags {
    name: string;
    description?: string;
    externalDocs?: ITCISwaggerExternalDocs;
}

export interface ITCISwaggerReference {
    $ref: string;
}

export interface ITCISwaggerDefinitionsDictionary {
    [name: string]: any;
}

export interface ITCISwaggerParametersDictionary {
    [name: string]: ITCISwaggerParameter;
}

export interface ITCISwaggerSecurityDefinitions {
    [name: string]: ITCISwaggerSecurity;
}

export interface ITCISwaggerSecurity {
    type: string;
    description?: string;
    name: string;
    in: string;
    authorizationUrl: string;
    tokenUrl: string;
    scopes: ITCISwaggerScopesDictionary;
    [key: string]: any;
}

export interface ITCISwaggerScopesDictionary {
    [name: string]: string;
}

export interface ITCISwagger {
    swagger: string;
    info: ITCISwaggerInfo;
    host?: string;
    basePath?: string;
    schemes?: string[];
    consumes?: string[];
    produces?: string[];
    paths: ITCISwaggerPathsDictionary;
    definitions?: ITCISwaggerDefinitionsDictionary;
    parameters?: ITCISwaggerParametersDictionary;
    responses?: ITCISwaggerResponseDictionary;
    securityDefinitions?: ITCISwaggerSecurityDefinitions;
    security?: ITCISwaggerSecurity[];
    tags?: ITCISwaggerTags[];
    externalDocs?: ITCISwaggerExternalDocs;

    getAppName(): string;

    getPaths(): string[];

    getMethods(path: string): string[];

    getParameters(path: string, method: string): any[];

    getResponse(path: string, method: string, code?: string | number): any;

    resolveRefs($ref): any;

    getMethodSummary(path: string, method: string): string;

    getOperationId(path: string, method: string): string;
}

export class Swagger implements ITCISwagger {

    swagger: string;
    info: ITCISwaggerInfo;
    host?: string;
    basePath?: string;
    schemes?: string[];
    consumes?: string[];
    produces?: string[];
    paths: ITCISwaggerPathsDictionary;
    definitions?: ITCISwaggerDefinitionsDictionary;
    parameters?: ITCISwaggerParametersDictionary;
    responses?: ITCISwaggerResponseDictionary;
    securityDefinitions?: ITCISwaggerSecurityDefinitions;
    security?: ITCISwaggerSecurity[];
    tags?: ITCISwaggerTags[];
    externalDocs?: ITCISwaggerExternalDocs;

    constructor(spec: any) {
        this.update(spec);
    }

    update(spec: any) {
        this.swagger = spec.swagger || this.swagger;
        this.info = spec.info || this.info;
        this.host = spec.host || this.host || "";
        this.basePath = spec.basePath || this.basePath || "";
        this.schemes = spec.schemes || this.schemes || [];
        this.consumes = spec.consumes || this.consumes || [];
        this.produces = spec.produces || this.produces || [];
        this.paths = spec.paths || this.paths || {};
        this.definitions = spec.definitions || this.definitions || {};
        this.parameters = spec.parameters || this.parameters || {};
        this.responses = spec.responses || this.responses || {};
        this.securityDefinitions = spec.securityDefinitions || this.securityDefinitions || {};
        this.security = spec.security || this.security || [];
        this.tags = spec.tags || this.tags || [];
        this.externalDocs = spec.externalDocs || this.externalDocs || {};
        return this;
    }

    getAppName(): string {
        return this.info.title;
    }

    getPaths(): string[] {
        const paths = [];
        for (const path in this.paths) {
            paths.push(path);
        }
        return paths;
    }

    getMethods(path: string): string[] {
        const methods = [];
        if (this.paths[path]) {
            for (const method in this.paths[path]) {
                methods.push(method);
            }
        }
        return methods;
    }

    getParameters(path: string, method: string): any[] {
        let parameters = [];
        if (this.paths[path] && this.paths[path][method] && typeof this.paths[path][method] !== "string") {
            try {
                parameters = (<any>this.paths[path][method]).parameters;
            } catch (e) {
                return parameters;
            }
            return parameters;
        }
    }

    getResponse(path: string, method: string, code?: string | number): any {
        let response = {};
        if (code && code.toString()) {
            if (this.paths[path] && this.paths[path][method] && typeof this.paths[path][method] !== "string") {
                try {
                    if ((<any>this.paths[path][method]).responses["200"]) {
                        response = (<any>this.paths[path][method]).responses[code];
                    } else if ((<any>this.paths[path][method]).responses["500"]) {
                        response = (<any>this.paths[path][method]).responses["500"];
                    }

                } catch (e) {
                    return response;
                }
                return response;
            }
        } else {
            return this.getResponse(path, method, "200");
        }
    }


    getDefaultResponseCode(path: string, method: string) {
        const response = "200";
        if (this.paths[path] && this.paths[path][method] && typeof this.paths[path][method] !== "string") {
            try {
                if ((<any>this.paths[path][method]).responses["200"]) {
                    return "200";
                } else if ((<any>this.paths[path][method]).responses["500"]) {
                    return "500";
                }
            } catch (e) {
                return response;
            }
            return response;
        }
    }

    resolveRefs($ref): any {
        let schema = {};
        const pathParams = $ref.split("/");
        if (pathParams[0] === "#" && pathParams[1] === "definitions") {
            for (let i = 2; i < pathParams.length; i++) {
                if (this.definitions[pathParams[i]]) {
                    schema = this.definitions[pathParams[i]];
                } else {
                    return "Invalid $ref";
                }
            }
            return schema;
        } else {
            return "Invalid $ref";
        }
    }

    getMethodSummary(path: string, method: string): string {
        let name = "";
        if (this.paths[path] && this.paths[path][method] && typeof this.paths[path][method] !== "string") {
            name = (<any>this.paths[path][method]).summary || "";
        }
        return name;
    }

    getOperationId(path: string, method: string): string {
        let name = "";
        if (this.paths[path] && this.paths[path][method] && typeof this.paths[path][method] !== "string") {
            name = (<any>this.paths[path][method]).operationId || "";
        }
        return name;
    }
}
